import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DefaultStoreProjectAssigneesService } from "@/services/defaultStoreProjectAssignees";
import { memo, useCallback, useMemo } from "react";
import { DefaultStoreProjectAssigneeListItem } from "@/types/defaultStoreProjectAssignee";
import { useDataTableSettings } from "@/components/data-table/hooks/useDataTableSettings";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { DefaultStoreProjectAssigneesDeleteDialogIdle } from "./DefaultStoreProjectAssigneesDeleteDialogIdle";
import {
  AsyncOperationProgress,
  OperationProgressItem,
  OperationProgressStatus,
} from "@/components/progress";
import { useAsyncOperationProgress } from "@/components/progress/useAsyncOperationProgress";
import { AsyncOperationItem } from "@/components/progress/AsyncOperationItem";

interface DefaultStoreProjectAssigneesDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedIds: string[];
  onDeleteSuccess?: () => void;
}

const DefaultStoreProjectAssigneesDeleteDialogComponent = ({
  open,
  onOpenChange,
  selectedIds,
  onDeleteSuccess,
}: DefaultStoreProjectAssigneesDeleteDialogProps) => {
  const { table } = useDataTableSettings<DefaultStoreProjectAssigneeListItem>();
  const tableData = useMemo(
    () => table.getRowModel().rows.map((row) => row.original),
    [table],
  );

  const getItemName = useCallback(
    (id: string) => {
      const item = tableData.find((item) => item.id === id);
      return item
        ? `${item.user_name || ""} - ${item.store_name || ""} - ${item.project_type_name || ""}`
        : id;
    },
    [tableData],
  );

  const renderCompletedItem = useCallback(
    (item: OperationProgressItem) => {
      return (
        <AsyncOperationItem
          label={getItemName(item.id)}
          success={item.success}
        />
      );
    },
    [getItemName],
  );

  const operationProgress = useAsyncOperationProgress();

  // Extract stable values and functions
  const {
    status,
    initialize,
    startProcessing,
    setCurrentItem,
    markItemProcessed,
    complete,
  } = operationProgress;

  const executeDelete = useCallback(
    async (ids: string[]) => {
      // Initialize progress with items
      const items = ids.map((id) => ({
        id,
        label: getItemName(id),
      }));
      initialize(items);
      startProcessing();

      // Process items sequentially
      for (const id of ids) {
        // isCancelling is not memoized, but safe to access directly
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (operationProgress.isCancelling()) {
          break;
        }

        const itemLabel = getItemName(id);
        setCurrentItem(itemLabel);

        try {
          await DefaultStoreProjectAssigneesService.delete(id);
          markItemProcessed(id, true);
        } catch (error) {
          markItemProcessed(id, false);
          // Continue processing other items even if one fails
        }
      }

      // Complete the operation
      complete();
    },
    // operationProgress is intentionally omitted - isCancelling is not memoized but safe to access
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      getItemName,
      initialize,
      startProcessing,
      setCurrentItem,
      markItemProcessed,
      complete,
    ],
  );

  const deleteMutation = useMutationWithErrorHandling({
    mutationFn: executeDelete,
    onError: complete,
  });

  const handleStartDelete = useCallback(() => {
    deleteMutation.mutate(selectedIds);
  }, [deleteMutation, selectedIds]);

  const onCancel = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);

  const onReset = useCallback(() => {
    onDeleteSuccess?.();
  }, [onDeleteSuccess]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] flex flex-col">
        {status === OperationProgressStatus.IDLE && (
          <DefaultStoreProjectAssigneesDeleteDialogIdle
            selectedIdsCount={selectedIds.length}
            onCancel={onCancel}
            onConfirm={handleStartDelete}
            isPending={deleteMutation.isPending}
          />
        )}
        <AsyncOperationProgress
          operationProgress={operationProgress}
          currentItemLabel={operationProgress.currentItemLabel}
          onClose={onReset}
          translationKey="defaultStoreProjectAssignees.deleteDialog.progress"
          renderCompletedItem={renderCompletedItem}
        />
      </DialogContent>
    </Dialog>
  );
};

export const DefaultStoreProjectAssigneesDeleteDialog = memo(
  DefaultStoreProjectAssigneesDeleteDialogComponent,
);
